<!-- src/views/tasas/TasasCambioList.vue -->
<template>
  <div class="p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Tasas de Cambio</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra las tasas de compra (USDT) y venta (USD)</p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Botón Imprimir con menú desplegable -->
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="btn-secondary"
          >
            <Icon icon="mdi:printer" />
            Imprimir
            <Icon icon="mdi:chevron-down" />
          </button>
          
          <!-- Menú Exportar / Imprimir -->
          <div v-if="showExportMenu" 
               class="absolute right-0 mt-2 w-64 bg-gray-900 rounded-xl shadow-lg border border-gray-700 z-50">
            <div class="py-2">
              <button
                @click="printFullReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-500" />
                <div>
                  <p class="font-medium">Reporte Completo</p>
                  <p class="text-xs text-gray-400">Con estadísticas y gráficos</p>
                </div>
              </button>
              <button
                @click="printSimplifiedReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:table" class="mr-3 text-blue-500" />
                <div>
                  <p class="font-medium">Listado Simplificado</p>
                  <p class="text-xs text-gray-400">Solo tabla de tasas</p>
                </div>
              </button>
              <div class="border-t border-gray-700 my-2"></div>
              <button
                @click="printCurrentView"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:eye" class="mr-3 text-green-500" />
                <div>
                  <p class="font-medium">Vista Actual</p>
                  <p class="text-xs text-gray-400">Con filtros aplicados</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <button
          @click="cargarDatos"
          class="btn-secondary"
          :disabled="loading"
        >
          <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" />
          Actualizar
        </button>
        
        <button
          @click="abrirModalNueva"
          class="btn-primary"
        >
          <Icon icon="mdi:plus" />
          Nueva Tasa
        </button>
      </div>
    </div>

    <!-- Selector de tipo de tasa (tabs) -->
    <div class="premium-card p-1.5 mb-6 flex gap-1">
      <button
        @click="tipoActivo = 'compra'"
        :class="['flex-1 py-2.5 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all',
          tipoActivo === 'compra' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200']"
      >
        <Icon icon="mdi:cash-plus" class="inline text-base mr-1" />
        Tasas de Compra (USDT)
      </button>
      <button
        @click="tipoActivo = 'venta'"
        :class="['flex-1 py-2.5 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all',
          tipoActivo === 'venta' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200']"
      >
        <Icon icon="mdi:cash-minus" class="inline text-base mr-1" />
        Tasas de Venta (USD)
      </button>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="premium-card p-4 mb-4">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div class="col-span-12 md:col-span-4">
          <label class="label-premium">Buscar Tasas</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input v-model="busqueda" type="text" placeholder="Buscar por fuente, observaciones..."
              class="input-premium input-with-icon h-10 text-xs"
              @input="handleSearch" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="label-premium">Estado</label>
          <select v-model="filtroEstado" class="input-premium h-10 text-xs cursor-pointer">
            <option value="todas">Todas</option>
            <option value="activas">Activas</option>
            <option value="inactivas">Inactivas</option>
          </select>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="label-premium">Fuente</label>
          <select v-model="filtroFuente" class="input-premium h-10 text-xs cursor-pointer">
            <option :value="null">Todas</option>
            <option v-for="fuente in fuentes" :key="fuente.idFuenteTasa" :value="fuente.idFuenteTasa">{{ fuente.nombre }}</option>
          </select>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="label-premium">Ordenar Por</label>
          <select v-model="ordenPor" class="input-premium h-10 text-xs cursor-pointer">
            <option value="fecha_desc">Más recientes</option>
            <option value="fecha">Más antiguas</option>
            <option value="fuente">Fuente (A-Z)</option>
            <option value="precio_desc">Mayor precio</option>
            <option value="precio">Menor precio</option>
          </select>
        </div>
        <div class="col-span-6 md:col-span-2">
          <button @click="limpiarFiltros" class="w-full btn-secondary h-10 text-xs justify-center">
            <Icon icon="mdi:filter-off" />
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <!-- Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-blue-500/10 rounded-xl">
          <Icon icon="mdi:cash-multiple" class="text-blue-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">Total Tasas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-tight">{{ totalTasas }}</p>
        </div>
      </div>
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-emerald-500/10 rounded-xl">
          <Icon icon="mdi:check-circle" class="text-emerald-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">Activas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-tight">{{ estadisticas.activas }}</p>
        </div>
      </div>
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-rose-500/10 rounded-xl">
          <Icon icon="mdi:close-circle" class="text-rose-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">Inactivas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-tight">{{ estadisticas.inactivas }}</p>
        </div>
      </div>
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-purple-500/10 rounded-xl">
          <Icon icon="mdi:tag" class="text-purple-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">Fuentes</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-tight">{{ fuentes.length }}</p>
        </div>
      </div>
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-amber-500/10 rounded-xl">
          <Icon icon="mdi:calendar" class="text-amber-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">Última Act.</p>
          <p class="text-sm font-black text-gray-800 dark:text-white leading-tight">{{ ultimaActualizacion }}</p>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="busqueda" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ busqueda }}"
          <button @click="busqueda = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filtroEstado !== 'todas'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filtroEstado === 'activas' ? 'Activas' : 'Inactivas' }}
          <button @click="filtroEstado = 'todas'" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filtroFuente" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
          Fuente: {{ obtenerNombreFuente(filtroFuente) }}
          <button @click="filtroFuente = null" class="ml-1.5 hover:text-purple-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="limpiarFiltros"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar todo
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
      <div class="flex flex-col items-center">
        <div class="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 mb-4"></div>
        <p class="text-gray-600 dark:text-gray-400">Cargando tasas de cambio...</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
      <div class="flex flex-col items-center">
        <div class="p-4 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
          <Icon icon="mdi:alert-circle" class="text-5xl text-red-500" />
        </div>
        <h3 class="text-xl font-bold text-gray-800 dark:text-white mb-2">Error al cargar datos</h3>
        <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">{{ error }}</p>
        <button
          @click="cargarDatos"
          class="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Icon icon="mdi:refresh" class="mr-2 inline" />
          Reintentar
        </button>
      </div>
    </div>

    <!-- Tabla de tasas -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
            {{ tipoActivo === 'compra' ? 'Tasas de Compra' : 'Tasas de Venta' }}
          </h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ paginatedTasas.length }} de {{ tasasFiltradas.length }} tasas
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <!-- Tabla de compra -->
      <div v-if="tipoActivo === 'compra'" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fuente</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Precio (Bs/USDT)</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Observaciones</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="tasasCompraFiltradas.length === 0">
              <td colspan="6" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:cash-remove" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No hay tasas de compra
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    No se encontraron tasas de compra con los filtros aplicados
                  </p>
                  <button
                    @click="abrirModalNueva"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primera tasa
                  </button>
                </div>
              </td>
            </tr>
            <tr 
              v-else 
              v-for="tasa in paginatedTasas" 
              :key="'compra-' + tasa.idTasaCompra"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="abrirModalDetalle('compra', tasa)"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ formatearFecha(tasa.fecha) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="obtenerColorFuente(tasa.nombreFuente)"
                >
                  {{ tasa.nombreFuente || 'N/A' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {{ formatearMonto(tasa.precio_usdt_bs, 2) }} Bs
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="tasa.activa ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                  @click.stop="toggleTasa('compra', tasa)"
                >
                  {{ tasa.activa ? 'Activa' : 'Inactiva' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                {{ tasa.observaciones || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="abrirModalEditar('compra', tasa)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    @click.stop="toggleTasa('compra', tasa)"
                    :class="[
                      'p-2 rounded-lg transition-colors',
                      tasa.activa
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
                    ]"
                    :title="tasa.activa ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="tasa.activa ? 'mdi:eye-off' : 'mdi:eye'" />
                  </button>
                  <button
                    @click.stop="confirmarEliminar('compra', tasa)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tabla de venta -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fuente</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Precio (Bs/USD)</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tasa Inversa</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Observaciones</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="tasasVentaFiltradas.length === 0">
              <td colspan="7" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:cash-remove" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No hay tasas de venta
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    No se encontraron tasas de venta con los filtros aplicados
                  </p>
                  <button
                    @click="abrirModalNueva"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primera tasa
                  </button>
                </div>
              </td>
            </tr>
            <tr 
              v-else 
              v-for="tasa in paginatedTasas" 
              :key="'venta-' + tasa.idTasaVenta"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="abrirModalDetalle('venta', tasa)"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ formatearFecha(tasa.fecha) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="obtenerColorFuente(tasa.nombreFuente)"
                >
                  {{ tasa.nombreFuente || 'N/A' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {{ formatearMonto(tasa.precio_usd_bs, 2) }} Bs
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                1 USD = {{ formatearMonto(1 / tasa.precio_usd_bs, 4) }} Bs
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="tasa.activa ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                  @click.stop="toggleTasa('venta', tasa)"
                >
                  {{ tasa.activa ? 'Activa' : 'Inactiva' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                {{ tasa.observaciones || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="abrirModalEditar('venta', tasa)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    @click.stop="toggleTasa('venta', tasa)"
                    :class="[
                      'p-2 rounded-lg transition-colors',
                      tasa.activa
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
                    ]"
                    :title="tasa.activa ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="tasa.activa ? 'mdi:eye-off' : 'mdi:eye'" />
                  </button>
                  <button
                    @click.stop="confirmarEliminar('venta', tasa)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación Premium -->
      <div v-if="tasasFiltradas.length > 0 && !loading" class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Mostrando <span class="font-semibold text-gray-700 dark:text-gray-300">{{ startItem }}–{{ endItem }}</span> de <span class="font-semibold text-gray-700 dark:text-gray-300">{{ tasasFiltradas.length }}</span>
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-left" class="text-base" />
          </button>
          <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
            :class="['w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all',
              currentPage === page
                ? 'bg-blue-600 text-white shadow-sm shadow-blue-300 dark:shadow-blue-900'
                : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700']">{{ page }}</button>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-right" class="text-base" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar tasa -->
    <BaseModal 
      v-model="showModal" 
      :title="modoEdicion ? 'Editar Tasa' : 'Nueva Tasa'"
      :icon="modoEdicion ? 'mdi:pencil' : 'mdi:plus'"
      iconType="primary"
      maxWidth="max-w-lg"
    >
      <form @submit.prevent="guardarTasa" class="space-y-6">
        <!-- Tipo de tasa (solo creación) -->
        <div v-if="!modoEdicion">
          <label class="label-premium">Tipo de Tasa *</label>
          <div class="flex p-1.5 bg-gray-100 dark:bg-gray-800 rounded-2xl">
            <button type="button" @click="tipoModal = 'compra'" :class="['flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all', tipoModal === 'compra' ? 'bg-white dark:bg-gray-700 text-blue-500 shadow-sm' : 'text-gray-400']">Compra</button>
            <button type="button" @click="tipoModal = 'venta'" :class="['flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all', tipoModal === 'venta' ? 'bg-white dark:bg-gray-700 text-blue-500 shadow-sm' : 'text-gray-400']">Venta</button>
          </div>
        </div>

        <div>
          <label class="label-premium">Fuente *</label>
          <div class="relative">
            <Icon icon="mdi:tag" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <select v-model="formData.idFuenteTasa" required class="input-premium input-with-icon cursor-pointer">
              <option value="" disabled>Seleccione una fuente</option>
              <option v-for="fuente in fuentesActivas" :key="fuente.idFuenteTasa" :value="fuente.idFuenteTasa">
                {{ fuente.nombre }} ({{ fuente.proposito || 'General' }})
              </option>
            </select>
          </div>
          <p v-if="errors.fuente" class="mt-2 text-xs text-red-500 font-bold uppercase">{{ errors.fuente }}</p>
        </div>

        <div>
          <label class="label-premium">{{ tipoModal === 'compra' ? 'Precio (Bs/USDT)' : 'Precio (Bs/USD)' }} *</label>
          <div class="relative">
            <Icon icon="mdi:cash" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input v-model.number="formData.precio" type="number" step="0.01" min="0.01" required class="input-premium input-with-icon !pr-12 font-black text-lg" placeholder="0.00" />
            <div class="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">Bs</div>
          </div>
          <p v-if="errors.precio" class="mt-2 text-xs text-red-500 font-bold uppercase">{{ errors.precio }}</p>
        </div>

        <div>
          <label class="label-premium">Observaciones</label>
          <textarea v-model="formData.observaciones" rows="3" maxlength="500" class="input-premium resize-none" placeholder="Notas adicionales..."></textarea>
        </div>

        <div v-if="modoEdicion">
          <label class="label-premium">Estado</label>
          <div class="flex p-1.5 bg-gray-100 dark:bg-gray-800 rounded-2xl">
            <button type="button" @click="formData.activa = true" :class="['flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all', formData.activa === true ? 'bg-white dark:bg-gray-700 text-emerald-500 shadow-sm' : 'text-gray-400']">Activa</button>
            <button type="button" @click="formData.activa = false" :class="['flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all', formData.activa === false ? 'bg-white dark:bg-gray-700 text-rose-500 shadow-sm' : 'text-gray-400']">Inactiva</button>
          </div>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button type="button" @click="cerrarModal" class="flex-1 btn-secondary">Cancelar</button>
          <button type="submit" :disabled="saving" class="flex-1 btn-primary">
            <Icon v-if="saving" icon="mdi:loading" class="animate-spin" />
            {{ saving ? 'Guardando...' : (modoEdicion ? 'Actualizar' : 'Registrar') }}
          </button>
        </div>
      </form>
    </BaseModal>

    <!-- Modal de detalles -->
    <Teleport to="body">
    <div v-if="showDetailModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div class="p-8">
          <div class="flex justify-between items-start mb-6">
            <div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Detalles de la Tasa</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">Información completa de la tasa</p>
            </div>
            <button @click="closeDetailModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Icon icon="mdi:close" class="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div v-if="selectedTasa" class="space-y-6">
            <div class="flex items-center space-x-4">
              <div class="p-4 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
                <Icon icon="mdi:cash" class="text-white text-2xl" />
              </div>
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ selectedTasa.nombreFuente }}
                </h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ tipoDetalle === 'compra' ? 'Tasa de Compra' : 'Tasa de Venta' }}
                </p>
              </div>
            </div>
            
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</label>
                  <p class="mt-1 text-gray-900 dark:text-white">
                    {{ tipoDetalle === 'compra' ? selectedTasa.idTasaCompra : selectedTasa.idTasaVenta }}
                  </p>
                </div>
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha</label>
                  <p class="mt-1 text-gray-900 dark:text-white">
                    {{ formatearFechaCompleta(selectedTasa.fecha) }}
                  </p>
                </div>
              </div>

              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Precio
                </label>
                <p class="mt-1 text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {{ formatearMonto(tipoDetalle === 'compra' ? selectedTasa.precio_usdt_bs : selectedTasa.precio_usd_bs, 2) }} Bs
                </p>
              </div>

              <div v-if="tipoDetalle === 'venta'">
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tasa Inversa
                </label>
                <p class="mt-1 text-gray-900 dark:text-white">
                  1 USD = {{ formatearMonto(1 / selectedTasa.precio_usd_bs, 4) }} Bs
                </p>
              </div>

              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fuente</label>
                <p class="mt-1">
                  <span 
                    class="px-3 py-1.5 text-xs font-medium rounded-full"
                    :class="obtenerColorFuente(selectedTasa.nombreFuente)"
                  >
                    {{ selectedTasa.nombreFuente }}
                  </span>
                </p>
              </div>

              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</label>
                <p class="mt-1">
                  <span 
                    class="px-3 py-1.5 text-xs font-medium rounded-full"
                    :class="selectedTasa.activa ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                  >
                    {{ selectedTasa.activa ? 'Activa' : 'Inactiva' }}
                  </span>
                </p>
              </div>

              <div v-if="selectedTasa.observaciones">
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Observaciones
                </label>
                <p class="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  {{ selectedTasa.observaciones }}
                </p>
              </div>
              
              <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div class="flex justify-end space-x-3">
                  <button
                    @click="abrirModalEditar(tipoDetalle, selectedTasa); closeDetailModal()"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar
                  </button>
                  <button
                    @click="closeDetailModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import { useModalStore } from "@/store/modal";
import BaseModal from "@/components/common/BaseModal.vue";

import { obtenerFuentes } from '@/api/fuenteTasa.js';
import { 
  obtenerTasasCompra, 
  crearTasaCompra, 
  actualizarTasaCompra, 
  eliminarTasaCompra, 
  activarTasaCompra, 
  desactivarTasaCompra 
} from '@/api/tasaCompra.js';
import { 
  obtenerTasasVenta, 
  crearTasaVenta, 
  actualizarTasaVenta, 
  eliminarTasaVenta, 
  activarTasaVenta, 
  desactivarTasaVenta 
} from '@/api/tasaVenta.js';

// Importar generador de reportes
import {
  imprimirReporteTasas,
  imprimirReporteTasasSimplificado,
  cargarLogo
} from '@/utils/reporteTasasGenerator.js';

export default {
  name: 'TasasCambioList',
  components: {
    Icon,
    BaseModal
  },
  setup() {
    const toast = useToast();
    const modal = useModalStore();
    return { toast, modal };
  },
  data() {
    return {
      // Estados
      loading: true,
      guardando: false,
      error: null,
      
      // Datos
      fuentes: [],
      tasasCompra: [],
      tasasVenta: [],
      
      // Filtros
      tipoActivo: 'compra',
      filtroEstado: 'todas',
      filtroFuente: null,
      busqueda: '',
      ordenPor: 'fecha_desc',
      currentPage: 1,
      itemsPerPage: 10,
      
      // Control del menú de impresión
      showExportMenu: false,
      
      // Modal
      showModal: false,
      showDetailModal: false,
      modoEdicion: false,
      tipoModal: 'compra',
      elementoEditando: null,
      selectedTasa: null,
      tipoDetalle: 'compra',
      formData: {
        idFuenteTasa: '',
        precio: '',
        observaciones: '',
        activa: true
      },
      errors: {},
      
      // Otros
      lastUpdate: new Date()
    };
  },
  computed: {
    // Fuentes activas
    fuentesActivas() {
      return this.fuentes.filter(f => f.es_activa);
    },
    
    // Tasas filtradas según tipo activo
    tasasCompraFiltradas() {
      let filtradas = [...this.tasasCompra];
      
      // Filtrar por estado
      if (this.filtroEstado === 'activas') {
        filtradas = filtradas.filter(t => t.activa === true);
      } else if (this.filtroEstado === 'inactivas') {
        filtradas = filtradas.filter(t => t.activa === false);
      }
      
      // Filtrar por fuente
      if (this.filtroFuente) {
        filtradas = filtradas.filter(t => t.idFuenteTasa === this.filtroFuente);
      }
      
      // Filtrar por búsqueda
      if (this.busqueda) {
        const termino = this.busqueda.toLowerCase();
        filtradas = filtradas.filter(t => 
          (t.nombreFuente || '').toLowerCase().includes(termino) ||
          (t.observaciones || '').toLowerCase().includes(termino) ||
          t.precio_usdt_bs.toString().includes(termino)
        );
      }
      
      // Ordenar
      return this.ordenarTasas(filtradas);
    },
    
    tasasVentaFiltradas() {
      let filtradas = [...this.tasasVenta];
      
      // Filtrar por estado
      if (this.filtroEstado === 'activas') {
        filtradas = filtradas.filter(t => t.activa === true);
      } else if (this.filtroEstado === 'inactivas') {
        filtradas = filtradas.filter(t => t.activa === false);
      }
      
      // Filtrar por fuente
      if (this.filtroFuente) {
        filtradas = filtradas.filter(t => t.idFuenteTasa === this.filtroFuente);
      }
      
      // Filtrar por búsqueda
      if (this.busqueda) {
        const termino = this.busqueda.toLowerCase();
        filtradas = filtradas.filter(t => 
          (t.nombreFuente || '').toLowerCase().includes(termino) ||
          (t.observaciones || '').toLowerCase().includes(termino) ||
          t.precio_usd_bs.toString().includes(termino)
        );
      }
      
      // Ordenar
      return this.ordenarTasas(filtradas);
    },
    
    tasasFiltradas() {
      return this.tipoActivo === 'compra' ? this.tasasCompraFiltradas : this.tasasVentaFiltradas;
    },
    
    totalTasas() {
      return this.tipoActivo === 'compra' ? this.tasasCompra.length : this.tasasVenta.length;
    },
    
    totalFiltradas() {
      return this.tasasFiltradas.length;
    },
    
    // Estadísticas
    estadisticas() {
      const tasas = this.tipoActivo === 'compra' ? this.tasasCompra : this.tasasVenta;
      const activas = tasas.filter(t => t.activa === true).length;
      const inactivas = tasas.filter(t => t.activa === false).length;
      const total = tasas.length;
      
      return {
        activas,
        inactivas,
        porcentajeActivas: total > 0 ? Math.round((activas / total) * 100) : 0,
        porcentajeInactivas: total > 0 ? Math.round((inactivas / total) * 100) : 0
      };
    },
    
    totalRecientes() {
      const sieteDiasAtras = new Date();
      sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
      
      const tasas = this.tipoActivo === 'compra' ? this.tasasCompra : this.tasasVenta;
      return tasas.filter(t => {
        try {
          const fecha = new Date(t.fecha);
          return fecha >= sieteDiasAtras;
        } catch {
          return false;
        }
      }).length;
    },
    
    ultimaActualizacion() {
      return this.formatTime(this.lastUpdate);
    },
    
    // Paginación
    paginatedTasas() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.tasasFiltradas.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.tasasFiltradas.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.tasasFiltradas.length);
    },
    
    // Validación formulario
    isFormValid() {
      return this.formData.idFuenteTasa && this.formData.precio > 0;
    },
    
    // Filtros activos
    isFiltered() {
      return this.busqueda || this.filtroEstado !== 'todas' || this.filtroFuente;
    }
  },
  async mounted() {
    // Cargar el logo para los reportes
    try {
      await cargarLogo();
      console.log('✅ Logo cargado correctamente');
    } catch (error) {
      console.error('Error cargando el logo:', error);
    }
    
    await this.cargarDatos();
    
    // Cerrar menú al hacer clic fuera
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    filtroEstado() {
      this.currentPage = 1;
    },
    filtroFuente() {
      this.currentPage = 1;
    },
    busqueda() {
      this.currentPage = 1;
    },
    ordenPor() {
      this.currentPage = 1;
    },
    tipoActivo() {
      this.currentPage = 1;
    }
  },
  methods: {
    // Manejar clic fuera del menú
    handleClickOutside(event) {
      const exportMenu = this.$el.querySelector('.relative');
      if (exportMenu && !exportMenu.contains(event.target) && this.showExportMenu) {
        this.showExportMenu = false;
      }
    },
    
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      
      try {
        // Cargar fuentes
        const fuentesRes = await obtenerFuentes(true);
        if (fuentesRes.success) {
          this.fuentes = fuentesRes.data || [];
        }
        
        // Cargar tasas
        await this.cargarTasas();
        
        this.lastUpdate = new Date();
      } catch (error) {
        console.error('Error cargando datos:', error);
        this.error = error.message || 'Error al cargar los datos';
      } finally {
        this.loading = false;
      }
    },
    
    async cargarTasas() {
      try {
        const [compraRes, ventaRes] = await Promise.all([
          obtenerTasasCompra('all'),
          obtenerTasasVenta('all')
        ]);
        
        if (compraRes.success) {
          this.tasasCompra = compraRes.data || [];
        } else {
          console.warn('Error cargando tasas de compra:', compraRes.error);
        }
        
        if (ventaRes.success) {
          this.tasasVenta = ventaRes.data || [];
        } else {
          console.warn('Error cargando tasas de venta:', ventaRes.error);
        }
      } catch (error) {
        console.error('Error cargando tasas:', error);
        throw error;
      }
    },
    
    ordenarTasas(tasas) {
      const copia = [...tasas];
      
      switch (this.ordenPor) {
        case 'fecha_desc':
          return copia.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        case 'fecha':
          return copia.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        case 'fuente':
          return copia.sort((a, b) => (a.nombreFuente || '').localeCompare(b.nombreFuente || ''));
        case 'precio_desc':
          return copia.sort((a, b) => {
            const precioA = a.precio_usdt_bs || a.precio_usd_bs || 0;
            const precioB = b.precio_usdt_bs || b.precio_usd_bs || 0;
            return precioB - precioA;
          });
        case 'precio':
          return copia.sort((a, b) => {
            const precioA = a.precio_usdt_bs || a.precio_usd_bs || 0;
            const precioB = b.precio_usdt_bs || b.precio_usd_bs || 0;
            return precioA - precioB;
          });
        default:
          return copia;
      }
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    limpiarFiltros() {
      this.busqueda = '';
      this.filtroEstado = 'todas';
      this.filtroFuente = null;
      this.ordenPor = 'fecha_desc';
      this.currentPage = 1;
    },
    
    obtenerNombreFuente(id) {
      if (!id) return '';
      const fuente = this.fuentes.find(f => f.idFuenteTasa === id);
      return fuente ? fuente.nombre : '';
    },
    
    // Métodos de impresión
    printFullReport() {
      if (this.tasasFiltradas.length === 0) {
        this.showWarningAlert('No hay tasas para imprimir');
        return;
      }

      this.showExportMenu = false;
      
      try {
        const filtros = {
          estado: this.filtroEstado,
          fuente: this.filtroFuente ? this.obtenerNombreFuente(this.filtroFuente) : null,
          busqueda: this.busqueda,
          orden: this.ordenPor
        };
        
        const titulo = `REPORTE DE TASAS DE ${this.tipoActivo === 'compra' ? 'COMPRA (USDT → Bs)' : 'VENTA (USD → Bs)'} - SYSTEM JJ`;
        
        imprimirReporteTasas(
          this.tasasFiltradas,
          this.tipoActivo,
          titulo,
          filtros
        );
      } catch (error) {
        console.error('Error generando reporte:', error);
        this.showErrorAlert('Error al generar el reporte: ' + error.message);
      }
    },
    
    printSimplifiedReport() {
      if (this.tasasFiltradas.length === 0) {
        this.showWarningAlert('No hay tasas para imprimir');
        return;
      }

      this.showExportMenu = false;
      
      try {
        const titulo = `LISTADO DE TASAS DE ${this.tipoActivo === 'compra' ? 'COMPRA' : 'VENTA'} - SYSTEM JJ`;
        
        imprimirReporteTasasSimplificado(
          this.tasasFiltradas,
          this.tipoActivo,
          titulo
        );
      } catch (error) {
        console.error('Error generando reporte:', error);
        this.showErrorAlert('Error al generar el reporte: ' + error.message);
      }
    },
    
    printCurrentView() {
      // La vista actual es la misma que tasasFiltradas
      this.printFullReport();
    },
    
    // Modal creación/edición
    abrirModalNueva() {
      this.modoEdicion = false;
      this.tipoModal = this.tipoActivo;
      this.elementoEditando = null;
      this.formData = {
        idFuenteTasa: '',
        precio: '',
        observaciones: '',
        activa: true
      };
      this.errors = {};
      this.showModal = true;
    },
    
    abrirModalEditar(tipo, elemento) {
      this.modoEdicion = true;
      this.tipoModal = tipo;
      this.elementoEditando = elemento;
      
      this.formData = {
        idFuenteTasa: elemento.idFuenteTasa,
        precio: tipo === 'compra' ? elemento.precio_usdt_bs : elemento.precio_usd_bs,
        observaciones: elemento.observaciones || '',
        activa: elemento.activa
      };
      
      this.errors = {};
      this.showModal = true;
    },
    
    cerrarModal() {
      this.showModal = false;
      this.modoEdicion = false;
      this.elementoEditando = null;
      this.formData = {
        idFuenteTasa: '',
        precio: '',
        observaciones: '',
        activa: true
      };
      this.errors = {};
    },
    
    // Modal detalles
    abrirModalDetalle(tipo, tasa) {
      this.tipoDetalle = tipo;
      this.selectedTasa = tasa;
      this.showDetailModal = true;
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedTasa = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.formData.idFuenteTasa) {
        this.errors.fuente = 'Debe seleccionar una fuente';
      }
      
      if (!this.formData.precio || this.formData.precio <= 0) {
        this.errors.precio = 'El precio debe ser mayor a 0';
      } else if (this.formData.precio > 1000000) {
        this.errors.precio = 'El precio no puede ser mayor a 1,000,000';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async guardarTasa() {
      if (!this.validateForm()) {
        return;
      }
      
      this.guardando = true;
      
      try {
        let resultado;
        
        if (this.modoEdicion) {
          // ACTUALIZAR - Usar el formato exacto que espera el backend
          if (this.tipoModal === 'compra') {
            resultado = await actualizarTasaCompra(this.elementoEditando.idTasaCompra, {
              precio_usdt_bs: parseFloat(this.formData.precio),
              idFuenteTasa: parseInt(this.formData.idFuenteTasa),
              activa: this.formData.activa,
              observaciones: this.formData.observaciones || null
            });
          } else {
            resultado = await actualizarTasaVenta(this.elementoEditando.idTasaVenta, {
              precio_usd_bs: parseFloat(this.formData.precio),
              idFuenteTasa: parseInt(this.formData.idFuenteTasa),
              activa: this.formData.activa,
              observaciones: this.formData.observaciones || null
            });
          }
        } else {
          // CREAR
          let idUsuario = null;
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              idUsuario = user.idUsuario || user.id || null;
            }
          } catch (e) {
            console.warn('Error obteniendo usuario:', e);
          }
          
          if (this.tipoModal === 'compra') {
            resultado = await crearTasaCompra({
              precio_usdt_bs: parseFloat(this.formData.precio),
              idFuenteTasa: parseInt(this.formData.idFuenteTasa),
              observaciones: this.formData.observaciones || null,
              idUsuario: idUsuario,
              activa: true,
              precio_usdt_usd: 1
            });
          } else {
            resultado = await crearTasaVenta({
              precio_usd_bs: parseFloat(this.formData.precio),
              idFuenteTasa: parseInt(this.formData.idFuenteTasa),
              observaciones: this.formData.observaciones || null,
              idUsuario: idUsuario,
              activa: true
            });
          }
        }
        
        if (resultado.success) {
          this.cerrarModal();
          await this.cargarTasas();
          this.toast.success(this.modoEdicion ? 'Tasa actualizada correctamente' : 'Tasa creada correctamente');
        } else {
          throw new Error(resultado.error || 'Error al guardar');
        }
        
      } catch (error) {
        console.error('Error guardando tasa:', error);
        this.showErrorAlert(error.message || 'Error al guardar la tasa');
      } finally {
        this.guardando = false;
      }
    },
    
    async toggleTasa(tipo, tasa) {
      const nuevoEstado = !tasa.activa;
      const accion = nuevoEstado ? 'activar' : 'desactivar';
      
      const confirmed = await this.modal.confirm({
        title: `¿${nuevoEstado ? 'Activar' : 'Desactivar'} tasa?`,
        message: `¿Deseas ${accion} la tasa de "${tasa.nombreFuente}"?`,
        icon: 'mdi:help-circle',
        iconType: nuevoEstado ? 'success' : 'warning',
        confirmText: `Sí, ${accion}`
      });

      if (confirmed) {
        try {
          let toggleResult;
          
          if (tipo === 'compra') {
            if (tasa.activa) {
              toggleResult = await desactivarTasaCompra(tasa.idTasaCompra);
            } else {
              toggleResult = await activarTasaCompra(tasa.idTasaCompra);
            }
          } else {
            if (tasa.activa) {
              toggleResult = await desactivarTasaVenta(tasa.idTasaVenta);
            } else {
              toggleResult = await activarTasaVenta(tasa.idTasaVenta);
            }
          }
          
          if (toggleResult.success) {
            await this.cargarTasas();
            this.toast.success(`La tasa ha sido ${accion}da correctamente`);
          } else {
            throw new Error(toggleResult.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('Error cambiando estado:', error);
          this.showErrorAlert(error.message || 'Error al cambiar el estado');
        }
      }
    },
    
    async confirmarEliminar(tipo, tasa) {
      const texto = `${tasa.nombreFuente} - ${this.formatearMonto(tipo === 'compra' ? tasa.precio_usdt_bs : tasa.precio_usd_bs, 2)} Bs`;
      
      const confirmed = await this.modal.confirm({
        title: '¿Eliminar tasa?',
        message: `¿Estás seguro de eliminar la tasa "${texto}"? Esta acción no se puede deshacer.`,
        icon: 'mdi:alert-circle',
        iconType: 'danger',
        confirmText: 'Sí, eliminar',
        type: 'danger'
      });

      if (confirmed) {
        await this.eliminarTasa(tipo, tasa);
      }
    },
    
    async eliminarTasa(tipo, tasa) {
      try {
        let resultado;
        
        if (tipo === 'compra') {
          resultado = await eliminarTasaCompra(tasa.idTasaCompra);
        } else {
          resultado = await eliminarTasaVenta(tasa.idTasaVenta);
        }
        
        if (resultado.success) {
          await this.cargarTasas();
          this.toast.success('La tasa ha sido eliminada correctamente');
        } else {
          throw new Error(resultado.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('Error eliminando tasa:', error);
        this.showErrorAlert(error.message || 'Error al eliminar la tasa');
      }
    },
    
    // Utilidades de formato
    formatearFecha(fechaISO) {
      if (!fechaISO) return 'N/A';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return fechaISO;
      }
    },
    
    formatearFechaCompleta(fechaISO) {
      if (!fechaISO) return 'N/A';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch {
        return fechaISO;
      }
    },
    
    formatTime(fecha) {
      if (!fecha) return '';
      try {
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return '';
      }
    },
    
    formatearMonto(monto, decimales = 2) {
      if (monto === null || monto === undefined) return '0.00';
      return parseFloat(monto).toFixed(decimales);
    },
    
    obtenerColorFuente(nombre) {
      if (!nombre) return 'bg-gray-500 text-white';
      
      const colores = {
        'BCV': 'bg-blue-600 text-white',
        'USDT': 'bg-green-600 text-white',
        'PARALELO': 'bg-purple-600 text-white',
        'DÓLAR TODAY': 'bg-orange-600 text-white',
        'ENPARALELOVZLA': 'bg-pink-600 text-white'
      };
      
      const nombreUpper = nombre.toUpperCase();
      return colores[nombreUpper] || 'bg-indigo-600 text-white';
    },
    
    formatDateTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return fechaISO;
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>

<!-- Estilos globales para SweetAlert2 oscuro -->
<style>
.swal-dark-popup {
  border: 1px solid #333 !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
}

.swal-dark-title {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.swal-dark-html {
  color: #e5e7eb !important;
}

.swal-dark-html strong {
  color: #f59e0b !important;
}

.swal-dark-confirm {
  border-radius: 8px !important;
  font-weight: 500 !important;
  padding: 10px 24px !important;
}

.swal-dark-cancel {
  border-radius: 8px !important;
  font-weight: 500 !important;
  padding: 10px 24px !important;
  background: #374151 !important;
  color: white !important;
  border: none !important;
}

.swal-dark-cancel:hover {
  background: #4b5563 !important;
}

.swal2-popup {
  animation: zoomIn 0.3s ease-in-out;
}

@keyframes zoomIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>